import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";
import { db, storage } from "../firebase";
import { requiresDocumentReview } from "./documentReviewPolicy";

// What happens to the data attached to ownership parties that are removed (Edit diagram, Reset,
// client type change):
// - Approved documents: kept as "Former owner – Name (date)" under Other documents, or deleted.
// - Documents awaiting review (and rejected/draft uploads): always deleted.
// - Linked signatories: kept (unlinked, details stay) or removed, per signatory.
// - Screening results: removed by the screening sections themselves once the person is gone.

const clean = (value) => String(value ?? "").trim();

// Documents section party IDs of a removed row (see KycSectionDocuments deriveParties).
export function documentPartyIds(item) {
  const row = item?.row || {};
  const id = clean(row._rowId || row._docRef || row.name);
  if (item?.collection === "shareholders") return [`shareholder:${id}`];
  if (item?.collection === "ubos") return [`ubo:${id}`];
  if (item?.collection === "arrangements") {
    return [`trust:${id}`, ...(row.parties || [])
      .filter((p) => /^trustee/i.test(clean(p?.role)))
      .map((p) => `trustee:${id}:${clean(p?._rowId || p?.name)}`)];
  }
  if (item?.collection === "parties" && /^trustee/i.test(clean(row.role))) {
    const arrId = clean(item.arrangement?._rowId);
    return [`trustee:${arrId}:${clean(row._rowId || row.name)}`];
  }
  return [];
}

function partyIdOfRequirement(requirementKey) {
  const key = clean(requirementKey);
  const sep = key.includes("|") ? "|" : ":";
  const parts = key.split(sep).map(clean);
  return parts[0] === "party" && parts.length >= 3 ? parts.slice(1, -1).join(sep) : "";
}

const isApproved = (file) => clean(file?.review?.status) === "approved";

async function clientDocumentSubmissions(form) {
  const clientId = clean(form?.clientId);
  if (!clientId) return [];
  const direct = await getDoc(doc(db, "documentSubmissions", clientId));
  if (direct.exists()) return [{ id: direct.id, ...(direct.data() || {}) }];
  const filters = [where("clientId", "==", clientId), where("type", "==", "documents")];
  if (clean(form?.orgId)) filters.push(where("orgId", "==", clean(form.orgId)));
  if (clean(form?.accessScope) === "team" && clean(form?.teamId)) {
    filters.push(where("accessScope", "==", "team"), where("teamId", "==", clean(form.teamId)));
  } else if (clean(form?.accessScope) === "individual" && clean(form?.ownerUid)) {
    filters.push(where("accessScope", "==", "individual"), where("ownerUid", "==", clean(form.ownerUid)));
  }
  const snap = await getDocs(query(collection(db, "documentSubmissions"), ...filters, limit(10)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

/**
 * Documents attached to the removed parties: { approved: [...], other: [...] } per party key,
 * each file with its submission id. A failed lookup returns no documents (nothing is changed).
 */
export async function loadRemovedPartyDocuments(form, parties) {
  const byPartyId = new Map();
  (parties || []).forEach((party) => party.documentPartyIds.forEach((id) => byPartyId.set(id, party.key)));
  const result = {};
  if (!byPartyId.size) return result;
  let submissions = [];
  try {
    submissions = await clientDocumentSubmissions(form);
  } catch (error) {
    console.warn("Could not load the documents of removed parties:", error?.message || error);
    return result;
  }
  submissions.forEach((submission) => (submission.files || []).forEach((file) => {
    const partyKey = byPartyId.get(partyIdOfRequirement(file?.review?.requirementKey));
    if (!partyKey || !clean(file?.objectPath)) return;
    const entry = result[partyKey] || (result[partyKey] = { approved: [], other: [] });
    (isApproved(file) && requiresDocumentReview(file) ? entry.approved : entry.other)
      .push({ ...file, __submissionId: submission.id });
  }));
  return result;
}

/**
 * Applies the document choice: approved documents kept (relabelled as a former owner's, under
 * Other documents) or deleted; documents awaiting review and other uploads always deleted.
 */
export async function applyRemovedPartyDocuments(documentsByParty, parties, { keepApproved }) {
  const nameOf = new Map((parties || []).map((p) => [p.key, p.name]));
  const date = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const toDelete = new Map(); // objectPath → submissionId
  const toRelabel = new Map(); // objectPath → requirementText
  Object.entries(documentsByParty || {}).forEach(([partyKey, entry]) => {
    entry.other.forEach((file) => toDelete.set(clean(file.objectPath), file.__submissionId));
    entry.approved.forEach((file) => {
      if (keepApproved) {
        const text = clean(file?.review?.requirementText || file?.cardTitle);
        toRelabel.set(clean(file.objectPath), `Former owner – ${nameOf.get(partyKey) || "unnamed"} (${date})${text ? ` · ${text}` : ""}`);
      } else {
        toDelete.set(clean(file.objectPath), file.__submissionId);
      }
    });
  });
  if (!toDelete.size && !toRelabel.size) return;

  for (const objectPath of toDelete.keys()) {
    try {
      await deleteObject(storageRef(storage, objectPath));
    } catch (error) {
      if (error?.code !== "storage/object-not-found") throw error;
    }
  }
  const submissionIds = new Set([
    ...toDelete.values(),
    ...Object.values(documentsByParty || {}).flatMap((e) => e.approved.map((f) => f.__submissionId)),
  ].filter(Boolean));
  for (const submissionId of submissionIds) {
    const ref = doc(db, "documentSubmissions", submissionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const data = snap.data() || {};
    const files = (Array.isArray(data.files) ? data.files : [])
      .filter((file) => !toDelete.has(clean(file?.objectPath)))
      .map((file) => {
        const label = toRelabel.get(clean(file?.objectPath));
        if (!label) return file;
        const oldKey = clean(file?.review?.requirementKey);
        return { ...file, review: { ...(file.review || {}), requirementKey: `additional:former-owner:${oldKey}`, requirementText: label } };
      });
    const realFiles = files.filter((file) => clean(file?.objectPath));
    const pending = realFiles.some((file) => requiresDocumentReview(file) &&
      !["approved", "rejected"].includes(clean(file?.review?.status)));
    await updateDoc(ref, {
      files,
      status: !realFiles.length ? "awaiting_client" : pending ? "pending_review" : clean(data.status) === "stored" ? "stored" : "reviewed",
      updatedAt: serverTimestamp(),
    });
  }
}

/** Signatories linked to removed rows, with the choice applied: "keep" unlinks, "remove" deletes. */
export function linkedSignatories(ownership, parties) {
  const removedRows = new Set((parties || []).flatMap((p) => p.rows.map((r) => `${r.collection}:${clean(r.row?._rowId)}`)));
  return (ownership?.signatories || []).filter((s) => {
    const link = s?._personLink;
    const collection = link?.collection === "controllingPersonsNatural" ? "ubos" : link?.collection === "directShareholders" ? "shareholders" : link?.collection;
    return link?.rowId && removedRows.has(`${collection}:${clean(link.rowId)}`);
  });
}

export function applySignatoryChoices(signatories, choices) {
  return (signatories || [])
    .filter((s) => choices[clean(s?._rowId)] !== "remove")
    .map((s) => (choices[clean(s?._rowId)] === "keep" ? { ...s, _personLink: null } : s));
}

/**
 * Removed rows grouped into parties (a person who is both a direct shareholder and a controlling
 * person is one party), with their document party IDs.
 */
export function groupRemovedParties(removed) {
  const parties = [];
  (removed || []).forEach((item) => {
    const nodeId = clean(item.row?.treeNodeId);
    const existing = nodeId && parties.find((p) => p.nodeId === nodeId);
    if (existing) {
      existing.rows.push(item);
      existing.documentPartyIds.push(...documentPartyIds(item));
      return;
    }
    parties.push({
      key: `${item.collection}:${clean(item.row?._rowId) || clean(item.row?.name)}:${parties.length}`,
      nodeId,
      kind: item.kind,
      name: item.name || "unnamed",
      parentName: item.parentName || "",
      rows: [item],
      documentPartyIds: documentPartyIds(item),
    });
  });
  return parties;
}

/** Every ownership row and trust party, as removed items (Reset, or deleting on a type change). */
export function allOwnershipParties(ownership) {
  const o = ownership || {};
  const kindOf = (row) => {
    const type = clean(row?.holderType || row?.shareholderEntityType).toLowerCase();
    return type === "trust" || type === "foundation" ? type : type && type !== "individual" ? "company" : "individual";
  };
  const items = [];
  (Array.isArray(o.shareholders) ? o.shareholders : o.directShareholders || [])
    .filter((row) => clean(row?.name)).forEach((row) => items.push({ collection: "shareholders", row, kind: kindOf(row), name: clean(row.name) }));
  (Array.isArray(o.ubos) ? o.ubos : o.controllingPersonsNatural || [])
    .filter((row) => clean(row?.name)).forEach((row) => items.push({ collection: "ubos", row, kind: "individual", name: clean(row.name) }));
  (o.legalArrangements?.arrangements || []).forEach((arr) => {
    if (clean(arr?.name)) items.push({ collection: "arrangements", row: arr, kind: clean(arr.arrangementType) === "Foundation" ? "foundation" : "trust", name: clean(arr.name) });
  });
  return groupRemovedParties(items);
}
